import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getBackendUrl } from './backendUrl';
import { log } from '../telemetry/logger';
import { instrumentAxios, getRequestTrace } from '../telemetry/axios-instrumentation';
import { getToken, getRefreshToken, setToken, setRefreshToken, removeToken, removeRefreshToken } from '../utils/HelperFunctions';

// axios doesn't include `_retry` on its config types; we attach it after
// a failed auth refresh to prevent infinite retry loops.
type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string> | null = null;

// Forward a failed HTTP request to the observability pipeline, correlated to the
// request's trace via the ids stashed by the axios instrumentation.
const logHttpFailure = (error: AxiosError) => {
  try {
    const cfg: AxiosRequestConfig = error.config ?? {};
    const tc = getRequestTrace(error.config);
    log.error(
      'api request failed',
      {
        source: 'axios',
        method: (cfg.method || '').toUpperCase(),
        url: (cfg.url || '').split('?')[0],
        status: error.response?.status,
        correlation_id: (error.response?.headers as Record<string, string> | undefined)?.['x-request-id'],
        trace_id: tc?.trace_id,
        span_id: tc?.span_id,
      },
      error
    );
  } catch {
    /* never let telemetry break the request flow */
  }
};

const shouldSkipAuthRefresh = (config: AxiosRequestConfig = {}) => {
  const url = config?.url || '';
  return config?.headers?.['x-skip-auth-refresh'] || url.includes('/users/login') || url.includes('/auth/refresh-token');
};

// Function to create the Axios instance
const createApiInstance = async () => {
  const baseURL = await getBackendUrl();

  const api = axios.create({
    baseURL,
  });

  // Trace instrumentation: opens a CLIENT span per request and injects
  // traceparent so the backend continues the same distributed trace. No-op
  // until initTracing() runs (gated by OTEL_ENABLED).
  instrumentAxios(api);

  const accessToken = getToken();
  if (accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  api.interceptors.request.use(
    (config) => {
      const currentToken = getToken();
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = (error.config || {}) as RetryableRequestConfig;

      if (shouldSkipAuthRefresh(originalRequest)) {
        return Promise.reject(error);
      }

      if (error.response?.status !== 401 || originalRequest._retry) {
        // Genuine failure (non-401, or a 401 that already exhausted its retry).
        logHttpFailure(error as AxiosError);
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        removeToken();
        removeRefreshToken();
        localStorage.setItem('SSOLogin', 'false');
        delete api.defaults.headers.common['Authorization'];
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = api
            .post('/auth/refresh-token', { refreshToken }, { headers: { 'x-skip-auth-refresh': 'true' } })
            .then((response) => {
              const newToken = response?.data?.token;
              const newRefreshToken = response?.data?.refreshToken;

              if (!newToken || !newRefreshToken) {
                throw new Error('Invalid refresh response');
              }

              setToken(newToken);
              setRefreshToken(newRefreshToken);
              api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              return newToken;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        removeToken();
        removeRefreshToken();
        localStorage.setItem('SSOLogin', 'false');
        delete api.defaults.headers.common['Authorization'];
        log.warn('auth token refresh failed; session ended', {
          source: 'axios',
          url: (originalRequest.url || '').split('?')[0],
        });
        return Promise.reject(refreshError);
      }
    }
  );

  console.log('api url: ' + baseURL);
  return api;
};

// Export the Axios instance
export const api = await createApiInstance();
export default api;
